/**
 * BHPH Account Management API Service
 * Provides type-safe API calls for payment processing, collections, promises, and late fees.
 */

import axios from 'axios';
import api from './api';

// ===== Type Definitions =====

export interface BHPHAccount {
  id: string;
  tenant_id: string;
  customer_id?: string | null;
  account_id: string;
  deal_number: string;
  buyer_name: string;
  vehicle_year_make_model: string;
  account_status: 'PENDING' | 'ACTIVE' | 'PAID_OFF' | 'CLOSED' | 'REPO';
  total_balance_amount: number;
  balance_principal_amount: number;
  balance_interest_amount: number;
  balance_late_amount: number;
  balance_other_amount: number;
  balance_down_amount: number;
  payment_amount: number;
  payment_frequency: string;
  payment_due_date: string;
  days_past_due: number;
  last_payment_date: string | null;
  payment_count: number;
  activation_date: string | null;
  closure_date: string | null;
  closure_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentAllocation {
  total_payment: number;
  late_fees_applied: number;
  interest_applied: number;
  principal_applied: number;
  other_fees_applied: number;
  down_applied: number;
  remaining_unallocated: number;
}

export interface Payment {
  id: string;
  tenant_id: string;
  deal_identifier: string;
  signer_first_name: string;
  signer_last_name: string;
  paid_date: string;
  payment_method: string;
  paid_amount: number;
  payment_type: string;
  principal_applied_amt: number;
  interest_applied_amt: number;
  late_fees_applied_amt: number;
  other_fees_applied_amt: number;
  created_by_name: string;
  created_at: string;
}

export interface CollectionAction {
  id: string;
  tenant_id: string;
  account_id: string;
  action_type: string;
  action_date: string;
  contact_method: string | null;
  contact_outcome: string | null;
  notes: string | null;
  promise_amount: number | null;
  promise_date: string | null;
  promise_kept: boolean | null;
  follow_up_required: boolean;
  follow_up_date: string | null;
  created_by_name: string;
  created_at: string;
}

export interface WorklistItem {
  account_id: string;
  account_number: string;
  customer_name: string;
  vehicle: string;
  days_past_due: number;
  delinquency_bucket: 'current' | 'early' | 'moderate' | 'serious' | 'critical' | 'severe';
  total_balance: number;
  payment_amount: number;
  last_payment_date: string | null;
  payment_due_date: string | null;
  priority_score: number;
  last_action_type: string | null;
  last_action_date: string | null;
  recommended_action: string;
}

export interface CollectionStats {
  total_active_accounts: number;
  delinquent_accounts: number;
  current_accounts: number;
  delinquency_buckets: {
    early_1_7_days: number;
    moderate_8_15_days: number;
    serious_16_30_days: number;
    critical_31_60_days: number;
    severe_61_plus_days: number;
  };
  total_delinquent_balance: number;
  delinquency_rate: number;
}

export interface PromiseStats {
  total_promises: number;
  pending_count: number;
  kept_count: number;
  broken_count: number;
  fulfillment_rate: number;
  pending_promise_amount: number;
  broken_rate: number;
}

export interface LateFeeConfig {
  id: string;
  tenant_id: string;
  late_fee_amount: number;
  late_fee_percentage: number;
  use_percentage: boolean;
  grace_period_days: number;
  max_late_fees_per_period: number;
  min_balance_for_fee: number;
  max_fee_amount: number | null;
  enabled: boolean;
  apply_to_statuses: string;
  send_notification: boolean;
  notification_template: string | null;
  created_at: string;
  updated_at: string;
}

export interface LateFeeTransaction {
  id: string;
  tenant_id: string;
  account_id: string;
  fee_amount: number;
  days_past_due: number;
  payment_amount: number | null;
  transaction_type: 'AUTO' | 'MANUAL';
  applied_date: string;
  waived: boolean;
  waived_date: string | null;
  waived_reason: string | null;
  waived_by_name: string | null;
  applied_by_name: string;
  notes: string | null;
  created_at: string;
}

export interface LateFeeStats {
  total_fees_applied: number;
  total_fees_waived: number;
  total_fees_active: number;
  total_amount_applied: number;
  total_amount_waived: number;
  total_amount_collected: number;
  this_month_fees_count: number;
  this_month_fees_amount: number;
  waiver_rate: number;
}

// ===== Payment Processing API =====

export const processPayment = async (data: {
  account_id: string;
  payment_amount: number;
  payment_method: string;
  payment_date?: string;
  notes?: string;
}): Promise<{
  payment_id: string;
  account_id: string;
  receipt_number: string;
  payment_amount: number;
  payment_method: string;
  payment_date: string;
  allocation: PaymentAllocation;
  new_balance: number;
  balance_before: number;
  processed_by: string;
  processed_at: string;
  message: string;
}> => {
  const payload = {
    ...data,
    payment_date: data.payment_date ? `${data.payment_date}T00:00:00` : undefined,
  };
  const response = await api.post('/payments/process', payload);
  return response as any;
};

export const getPaymentHistory = async (
  accountId: string,
  limit: number = 100
): Promise<Payment[]> => {
  const response = await api.get(`/payments/history/${accountId}`, {
    params: { limit },
  });
  return response as any;
};

export const getPaymentReceipt = async (paymentId: string) => {
  const response = await api.get(`/payments/receipt/${paymentId}`);
  return response as any;
};

export const getPaymentSummary = async (accountId: string) => {
  const response = await api.get(`/payments/summary/${accountId}`);
  return response as any;
};

// ===== Collections API =====

export const getCollectionWorklist = async (
  bucket?: string
): Promise<{ worklist: WorklistItem[]; total_accounts: number }> => {
  const response = await api.get('/collections/worklist', {
    params: { bucket },
  });
  return response as any;
};

export const logCollectionAction = async (
  accountId: string,
  data: {
    action_type: string;
    contact_method?: string;
    contact_outcome?: string;
    notes?: string;
    promise_amount?: number;
    promise_date?: string;
    follow_up_required?: boolean;
    follow_up_date?: string;
  }
): Promise<CollectionAction> => {
  const response = await api.post(`/collections/${accountId}/action`, data);
  return response as any;
};

export const getCollectionHistory = async (
  accountId: string,
  limit: number = 50
): Promise<CollectionAction[]> => {
  const response = await api.get(`/collections/${accountId}/history`, {
    params: { limit },
  });
  return response as any;
};

export const getCollectionStats = async (): Promise<CollectionStats> => {
  const response = await api.get('/collections/stats');
  if (!response) {
    throw new Error('No data returned from collections stats endpoint');
  }
  return response as any;
};

export const getDelinquentAccounts = async (bucket?: string, minDays: number = 1) => {
  const response = await api.get('/collections/delinquent', {
    params: { bucket, min_days: minDays },
  });
  return response as any;
};

// ===== Promise Tracking API =====

export const getPendingPromises = async (accountId?: string): Promise<CollectionAction[]> => {
  const response = await api.get('/collections/promises/pending', {
    params: { account_id: accountId },
  });
  return response as any;
};

export const getBrokenPromises = async (sinceDays: number = 30): Promise<CollectionAction[]> => {
  const response = await api.get('/collections/promises/broken', {
    params: { since_days: sinceDays },
  });
  return response as any;
};

export const getPromiseStats = async (): Promise<PromiseStats> => {
  const response = await api.get('/collections/promises/stats');
  if (!response) {
    throw new Error('No data returned from promise stats endpoint');
  }
  return response as any;
};

export const markPromiseKept = async (
  actionId: string,
  notes?: string
): Promise<CollectionAction> => {
  const response = await api.put(`/collections/promises/${actionId}/mark-kept`, null, {
    params: { notes },
  });
  return response as any;
};

export const markPromiseBroken = async (
  actionId: string,
  notes?: string
): Promise<CollectionAction> => {
  const response = await api.put(`/collections/promises/${actionId}/mark-broken`, null, {
    params: { notes },
  });
  return response as any;
};

export const getPromiseReminders = async (daysAhead: number = 1) => {
  const response = await api.get('/collections/promises/reminders', {
    params: { days_ahead: daysAhead },
  });
  return response as any;
};

export const getAccountPromises = async (accountId: string): Promise<CollectionAction[]> => {
  const response = await api.get(`/collections/${accountId}/promises`);
  return response as any;
};

// ===== Late Fee API =====

export const getLateFeeConfig = async (): Promise<LateFeeConfig> => {
  const response = await api.get('/late-fees/config');
  return response as any;
};

export const updateLateFeeConfig = async (data: Partial<LateFeeConfig>): Promise<LateFeeConfig> => {
  const response = await api.put('/late-fees/config', data);
  return response as any;
};

export const applyLateFee = async (
  accountId: string,
  notes?: string
): Promise<LateFeeTransaction> => {
  const response = await api.post(`/late-fees/accounts/${accountId}/apply`, {
    notes,
  });
  return response as any;
};

export const waiveLateFee = async (
  transactionId: string,
  reason: string
): Promise<LateFeeTransaction> => {
  const response = await api.put(`/late-fees/transactions/${transactionId}/waive`, {
    reason,
  });
  return response as any;
};

export const getLateFeeHistory = async (
  accountId: string,
  limit: number = 50
): Promise<LateFeeTransaction[]> => {
  const response = await api.get(`/late-fees/accounts/${accountId}/history`, {
    params: { limit },
  });
  return response as any;
};

export const getLateFeeStats = async (): Promise<LateFeeStats> => {
  const response = await api.get('/late-fees/stats');
  if (!response) {
    throw new Error('No data returned from late fee stats endpoint');
  }
  return response as any;
};

export const getEligibleAccountsForLateFees = async () => {
  const response = await api.get('/late-fees/accounts/eligible');
  return response as any;
};

// ===== BHPH Account API =====

export const getBHPHAccount = async (accountId: string): Promise<BHPHAccount> => {
  const response = await api.get(`/bhph-accounts/${accountId}`);
  return response as any;
};

export const listBHPHAccounts = async (params?: {
  skip?: number;
  limit?: number;
  account_status?: string;
  buyer_name?: string;
}): Promise<BHPHAccount[]> => {
  const response = await api.get<BHPHAccount[]>('/bhph-accounts/', { params });
  return response;
};

export const searchBHPHAccount = async (searchQuery: string): Promise<BHPHAccount[]> => {
  try {
    const response = await api.get<BHPHAccount[]>(`/bhph-accounts/search`, {
      params: { q: searchQuery },
    });
    return response;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

export const createAccountFromDeal = async (dealId: string) => {
  const response = await api.post('/bhph-accounts/create-from-deal', {
    deal_id: dealId,
  });
  return response as any;
};

export const activateAccount = async (accountId: string, firstPaymentDueDate: string) => {
  const response = await api.put(`/bhph-accounts/${accountId}/activate`, {
    first_payment_due_date: firstPaymentDueDate,
  });
  return response as any;
};

export const closeAccount = async (accountId: string, reason: string) => {
  const response = await api.put(`/bhph-accounts/${accountId}/close`, {
    reason,
  });
  return response as any;
};

// ===== Utility Functions =====

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const parseDisplayDate = (dateString: string): Date => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return new Date(`${dateString}T00:00:00`);
  }

  return new Date(dateString);
};

export const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'N/A';
  return parseDisplayDate(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getDelinquencyBucketColor = (bucket: string): string => {
  const colors: Record<string, string> = {
    current: 'green',
    early: 'yellow',
    moderate: 'orange',
    serious: 'red',
    critical: 'purple',
    severe: 'red',
  };
  return colors[bucket] || 'gray';
};

export const getDelinquencyBucketLabel = (bucket: string): string => {
  const labels: Record<string, string> = {
    current: 'Current',
    early: 'Early (1-7 days)',
    moderate: 'Moderate (8-15 days)',
    serious: 'Serious (16-30 days)',
    critical: 'Critical (31-60 days)',
    severe: 'Severe (61+ days)',
  };
  return labels[bucket] || bucket;
};
